import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Eye, EyeOff, Lock, Shield, Monitor } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';

const Login = ({ mode = 'full', onLogin }) => {
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [appClosed, setAppClosed] = useState(false);
  const [appMessage, setAppMessage] = useState('');

  const isRAO = mode === 'rao';

  // Check app status on mount
  useEffect(() => {
    axios.get(`${API}/app-status`).then(resp => {
      if (!resp.data.active) {
        setAppClosed(true);
        setAppMessage(resp.data.message || "L'application est temporairement fermée.");
      }
    }).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const resp = await axios.post(`${API}/rao/auth/login`, { email: email.trim(), password });
      if (resp.data.success) {
        const student = resp.data.student;
        if (resp.data.token) localStorage.setItem('sim_token', resp.data.token);

        // Block non-admin students when app is closed
        if (appClosed && !student.permissions?.admin) {
          toast.error(appMessage || "L'application est temporairement fermée.");
          setPassword('');
          setIsLoading(false);
          return;
        }

        // Also double-check app status in real-time
        try {
          const statusResp = await axios.get(`${API}/app-status`);
          if (!statusResp.data.active && !student.permissions?.admin) {
            toast.error(statusResp.data.message || "L'application est temporairement fermée.");
            setPassword('');
            setIsLoading(false);
            return;
          }
        } catch { /* proceed if check fails */ }

        // For RAO mode, check RAO permission
        if (isRAO && student.permissions?.rao === false) {
          toast.error("Vous n'avez pas accès au module RAO. Contactez votre instructeur.");
          setPassword('');
          setIsLoading(false);
          return;
        }

        // For full mode, check if student has at least one simulator permission
        if (!isRAO) {
          const perms = student.permissions || {};
          const hasAnySimPerm = perms.scenarios || perms.appels_911 || perms.appels_assignes || perms.historique;
          if (!hasAnySimPerm && !perms.rao) {
            toast.error("Votre compte n'a aucun accès actif. Contactez votre instructeur.");
            setPassword('');
            setIsLoading(false);
            return;
          }
        }

        onLogin(student);
        toast.success('Connexion réussie');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Courriel ou mot de passe incorrect');
      setPassword('');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4"
         style={{ background: isRAO
           ? 'linear-gradient(135deg, #f8fbff 0%, #E0F8E0 50%, #CAE9FA 100%)'
           : 'linear-gradient(135deg, #f8fbff 0%, #CAE9FA 50%, #E0F8E0 100%)'
         }}>
      <div className="w-full max-w-md">
        {/* College Logo */}
        <div className="text-center mb-6">
          <img 
            src="/logo-montmorency.png" 
            alt="Collège Montmorency" 
            className="h-10 mx-auto opacity-80"
            data-testid="college-logo"
          />
        </div>

        <div className="text-center mb-8">
          <div className="w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-4"
               style={{ background: isRAO
                 ? 'linear-gradient(135deg, #24B219, #82F486)'
                 : 'linear-gradient(135deg, #002768, #2E6CF5)'
               }}>
            {isRAO ? <Monitor className="w-10 h-10 text-white" /> : <Shield className="w-10 h-10 text-white" />}
          </div>
          <h1 className="text-3xl font-bold mb-2" style={{ color: '#002768' }}>
            {isRAO ? 'Formation RAO' : 'Simulateur 911'}
          </h1>
          <p className="text-gray-600">
            {isRAO
              ? 'Répartition Assistée par Ordinateur'
              : "Formation des Répartiteurs d'Urgence"}
          </p>
        </div>

        <Card className="glass border-0 shadow-xl">
          <CardHeader className="text-center">
            <CardTitle className="flex items-center justify-center space-x-2">
              <Lock className="w-5 h-5" style={{ color: '#002768' }} />
              <span style={{ color: '#002768' }}>Connexion Étudiant</span>
            </CardTitle>
            <CardDescription>
              Identifiez-vous avec votre compte étudiant
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <input
                  type="text"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Courriel ou numéro d'étudiant"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2"
                  style={{ '--tw-ring-color': isRAO ? '#24B219' : '#2E6CF5' }}
                  disabled={isLoading}
                  data-testid="email-input"
                />
              </div>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Mot de passe"
                  className="w-full px-4 py-3 pr-12 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2"
                  style={{ '--tw-ring-color': isRAO ? '#24B219' : '#2E6CF5' }}
                  disabled={isLoading}
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>

              <button
                type="submit"
                disabled={!password.trim() || !email.trim() || isLoading}
                className="w-full py-3 text-white rounded-lg font-semibold hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                style={{ background: isRAO
                  ? 'linear-gradient(to right, #24B219, #82F486)'
                  : 'linear-gradient(to right, #002768, #2E6CF5)'
                }}
                data-testid="login-button"
              >
                {isLoading ? (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Vérification...</span>
                  </div>
                ) : (
                  isRAO ? 'Accéder à la Formation RAO' : 'Accéder au Simulateur'
                )}
              </button>
            </form>
          </CardContent>
        </Card>

        <div className="text-center mt-8">
          <p className="cm-slogan text-sm italic">Avance à ta façon</p>
        </div>
      </div>
    </div>
  );
};

export default Login;
